/**
 * Hook: fetch options from asyncConfig URL for select/radio/checkboxGroups.
 * Returns { options, loading }.
 * - task-owners: GET, no params
 * - task-assignees: POST with body from asyncConfig.body (when present) or fallback { taskOwners, tenantId, domainName }
 */
import { useState, useEffect, useRef } from "react";
import { apiClient } from "../../services/apiClient";
import { envConfig } from "../../config/env";
import type { OptionItem, AsyncConfig, FormData } from "./types";
import {
  isTaskAssigneesUrl,
  isTaskOwnersUrl,
  normalizeTaskOwnersResponseToOptions,
  normalizeTaskAssigneesResponseToOptions,
  normalizeAsyncResponseToOptions,
  getTaskAssigneesRequestCookies,
  normalizeTaskOwnersForRequest,
  buildBodyFromFormState,
  getDependencyValues,
} from "./formFieldUtils";

/**
 * Resolve async option URL for apiClient.
 * - Absolute http(s): unchanged.
 * - Paths starting with `/api/`: keep as relative `/api/...` so axios uses the instance baseURL
 *   (avoids `base + /api/...` doubling when base already ends with `/api`).
 * - Other relative paths: prepend asyncConfigBaseUrl or api base when set.
 */
function buildFullUrl(url: string): string {
  const base = (
    envConfig.asyncConfigBaseUrl ??
    apiClient.getBaseURL?.() ??
    ""
  ).replace(/\/$/, "");
  if (/^https?:\/\//i.test(url)) return url;

  const path = url.startsWith("/") ? url : `/${url}`;
  if (path.startsWith("/api/")) return path;

  return base ? `${base}${path}` : url;
}

function getAsyncPaths(asyncConfig: AsyncConfig) {
  return {
    labelPath: asyncConfig.labelPath ?? "name",
    valuePath: asyncConfig.valuePath ?? "code",
    listDataPath: asyncConfig.listDataPath,
  };
}

function shouldUsePost(asyncConfig: AsyncConfig, url: string): boolean {
  const isTaskAssignees = isTaskAssigneesUrl(url);
  const hasBodyConfig =
    Array.isArray(asyncConfig.body) && asyncConfig.body.length > 0;
  return (
    isTaskAssignees || (hasBodyConfig && asyncConfig.requestMethod === "POST")
  );
}

function buildPostBody(
  asyncConfig: AsyncConfig,
  url: string,
  formData: FormData,
): Record<string, unknown> {
  const isTaskAssignees = isTaskAssigneesUrl(url);
  const hasBodyConfig =
    Array.isArray(asyncConfig.body) && asyncConfig.body.length > 0;

  if (hasBodyConfig) {
    let body = buildBodyFromFormState(asyncConfig.body!, formData ?? {});
    if (isTaskAssignees) {
      const { tenantId, domainName } = getTaskAssigneesRequestCookies();
      body = { ...body, tenantId, domainName };
    }
    return body;
  }

  const { tenantId, domainName } = getTaskAssigneesRequestCookies();
  return {
    taskOwners: normalizeTaskOwnersForRequest((formData as any)?.taskOwners),
    tenantId,
    domainName,
  };
}

function buildGetParams(
  asyncConfig: AsyncConfig,
): Record<string, string> | undefined {
  const params: Record<string, string> = {};
  for (const p of asyncConfig.params ?? []) {
    if (p.fieldName != null) params[p.fieldName] = String(p.value ?? "");
  }
  return Object.keys(params).length > 0 ? params : undefined;
}

export function useAsyncOptions(
  asyncConfig: AsyncConfig | undefined,
  formData?: FormData,
): { options: OptionItem[]; loading: boolean } {
  const [options, setOptions] = useState<OptionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);
  const isConsoleDebugEnabled =
    typeof process === "undefined" || process.env.NODE_ENV !== "production";

  useEffect(() => {
    mounted.current = true;

    const url = asyncConfig?.url;
    if (!url) {
      setOptions([]);
      setLoading(false);
      return;
    }

    const { labelPath, valuePath, listDataPath } = getAsyncPaths(asyncConfig);

    setLoading(true);
    const fullUrl = buildFullUrl(url);
    const debugIsDepartmentConcept =
      isConsoleDebugEnabled &&
      url.includes("global_filter_search") &&
      (asyncConfig?.params ?? []).some(
        (p) => p.fieldName === "concept_name" && String(p.value) === "Department",
      );

    const safeSetOptions = (next: OptionItem[]) => {
      if (mounted.current) setOptions(next);
    };
    const safeSetLoading = (next: boolean) => {
      if (mounted.current) setLoading(next);
    };

    const runPost = async () => {
      const body = buildPostBody(asyncConfig, url, formData ?? {});
      try {
        if (debugIsDepartmentConcept) {
          console.log("[useAsyncOptions][department] POST start", {
            url,
            fullUrl,
            body,
          });
        }
        const res = await apiClient.post<unknown>(fullUrl, body);
        if (!mounted.current) return;
        const mapped = normalizeTaskAssigneesResponseToOptions(res, {
          labelPath,
          valuePath,
        });
        if (debugIsDepartmentConcept) {
          console.log("[useAsyncOptions][department] POST success", {
            url,
            fullUrl,
            mappedCount: mapped.length,
            sample: mapped.slice(0, 10),
          });
        }
        safeSetOptions(mapped);
      } catch {
        if (debugIsDepartmentConcept) {
          console.log("[useAsyncOptions][department] POST error", {
            url,
            fullUrl,
          });
        }
        safeSetOptions([]);
      } finally {
        safeSetLoading(false);
      }
    };

    const runGet = async () => {
      const params = buildGetParams(asyncConfig);
      try {
        if (debugIsDepartmentConcept) {
          console.log("[useAsyncOptions][department] GET start", {
            url,
            fullUrl,
            params,
          });
        }
        const res = await apiClient.get<unknown>(fullUrl, params);
        if (!mounted.current) return;
        const body =
          (res as { data?: unknown })?.data != null ? (res as any).data : res;
        const mapped = isTaskOwnersUrl(url)
          ? normalizeTaskOwnersResponseToOptions(res, { labelPath, valuePath })
          : normalizeAsyncResponseToOptions(body, {
              labelPath,
              valuePath,
              listDataPath,
            });
        if (debugIsDepartmentConcept) {
          console.log("[useAsyncOptions][department] GET success", {
            url,
            fullUrl,
            mappedCount: mapped.length,
            sample: mapped.slice(0, 10),
          });
        }
        safeSetOptions(mapped);
      } catch {
        if (debugIsDepartmentConcept) {
          console.log("[useAsyncOptions][department] GET error", {
            url,
            fullUrl,
            params,
          });
        }
        safeSetOptions([]);
      } finally {
        safeSetLoading(false);
      }
    };

    if (shouldUsePost(asyncConfig, url)) {
      void runPost();
    } else {
      void runGet();
    }

    return () => {
      mounted.current = false;
    };
  }, [
    asyncConfig?.url,
    asyncConfig?.listDataPath,
    asyncConfig?.labelPath,
    asyncConfig?.valuePath,
    asyncConfig?.requestMethod,
    JSON.stringify(asyncConfig?.params ?? []),
    JSON.stringify(asyncConfig?.body ?? []),
    asyncConfig?.dependencies?.join(","),
    JSON.stringify(
      asyncConfig?.dependencies?.length
        ? getDependencyValues(asyncConfig.dependencies, formData ?? {})
        : { formDataTaskOwners: formData?.taskOwners },
    ),
  ]);

  return { options, loading };
}
